<template>
  <v-row>
    <v-col cols="8">
      <v-data-table-server :headers="headers"
                           :items="pharmaPipelineItems"
                           v-model:items-per-page="itemsPerPage"
                           v-model:sort-by="sortBy"
                           :items-length="totalItems"
                           :loading="loading"
                           :search="search"
                           :row-props="rowProps"
                           :items-per-page-options="[15,25,50,100]"
                           :multi-sort="true"
                           @update:options="loadItems">
        <template v-slot:top>
          <v-toolbar color="secondary" flat>
            <v-toolbar-title class="text-button">Pharma Pipelines</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn variant="plain" icon="mdi-file-excel" :disabled="loading" @click.stop="downloadExcel"></v-btn>
          </v-toolbar>
        </template>
        <template v-slot:[`item.company`]="{ item }">
          <v-chip class="me-2" color="primary" size="small" :label="true"
                  @click.stop="toggleAllOneCompanies(item.company)">{{ item.company }}</v-chip>
        </template>
        <template v-slot:[`item.therapeuticArea`]="{ item }">
          <v-chip-group :column="true">
            <v-chip v-for="therapeuticArea in [...new Set(item.therapeuticArea)].sort()" :key="therapeuticArea"
                    class="me-2" color="primary" size="small" :label="true"
                    @click.stop="toggleAllOneTherapeuticAreas(therapeuticArea)">{{ therapeuticArea }}</v-chip>
          </v-chip-group>
        </template>
        <template v-slot:[`item.phase`]="{ item }">
          <v-chip class="me-2" color="primary" size="small" :label="true"
                  @click.stop="toggleAllOnePhases(item.phase)">{{ item.phase }}</v-chip>
        </template>
        <template v-slot:no-data>
          <v-card-text>No data available</v-card-text>
        </template>
      </v-data-table-server>
    </v-col>
    <v-col cols="4">
      <v-toolbar color="secondary" flat>
        <v-spacer></v-spacer>
        <v-label>Filters</v-label>
        <v-btn variant="text" icon="mdi-filter" @click.stop="filter_drawer = !filter_drawer"></v-btn>
      </v-toolbar>
      <v-row>
        <v-col cols="12">
          <v-card class="mx-auto" elevation="0">
            <div>
              <GChart type="PieChart"
                      @ready="companiesChartReady"
                      :data="companiesChartData"
                      :options="companiesChartOptions"
                      v-if="companiesChartData.length !== 0"/>
              <v-chip variant="elevated" size="x-large" density="default" v-else>
                No data to display. Please adjust your filters.
              </v-chip>
              <v-overlay v-model="loading"
                         class="align-center justify-center"
                         opacity="0"
                         contained>
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
              </v-overlay>
            </div>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-card class="mx-auto" elevation="0">
            <div>
              <GChart type="PieChart"
                      @ready="therapeuticAreasChartReady"
                      :data="therapeuticAreasChartData"
                      :options="therapeuticAreasChartOptions"
                      v-if="therapeuticAreasChartData.length !== 0"/>
              <v-chip variant="elevated" size="x-large" density="default" v-else>
                No data to display. Please adjust your filters.
              </v-chip>
              <v-overlay v-model="loading"
                         class="align-center justify-center"
                         opacity="0"
                         contained>
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
              </v-overlay>
            </div>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-card class="mx-auto" elevation="0">
            <div>
              <GChart type="PieChart"
                      @ready="phasesChartReady"
                      :data="phasesChartData"
                      :options="phasesChartOptions"
                      v-if="phasesChartData.length !== 0"/>
              <v-chip variant="elevated" size="x-large" density="default" v-else>
                No data to display. Please adjust your filters.
              </v-chip>
              <v-overlay v-model="loading"
                         class="align-center justify-center"
                         opacity="0"
                         contained>
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
              </v-overlay>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
  <v-navigation-drawer v-model="filter_drawer" location="right" v-bind:width="500">
    <v-card class="mx-auto" elevation="0">
      <v-row no-gutters>
        <v-col>
          <v-list nav dense light>
            <v-list-item prepend-icon="mdi-filter" title="Filters" nav></v-list-item>
            <v-divider></v-divider>
            <v-form ref="pharma_pipelines_filter_form">
              <div v-if="companyItems.length > 0">
                <v-row>
                  <v-col cols="12">
                    <v-combobox v-model="selectedCompanies" :items="companyItems"
                                label="Company" :rules="[rules.required]" @click="$refs.pharma_pipelines_filter_form.validate()"
                                chips multiple>
                    </v-combobox>

                  </v-col>
                </v-row>
                <v-divider :vertical="true"></v-divider>
              </div>
              <div v-if="therapeuticAreaItems.length > 0">
                <v-row>
                  <v-col cols="12">
                    <v-combobox v-model="selectedTherapeuticAreas" :items="therapeuticAreaItems"
                                label="Therapeutic area" :rules="[rules.required]" @click="$refs.pharma_pipelines_filter_form.validate()"
                                chips multiple>
                    </v-combobox>

                  </v-col>
                </v-row>
                <v-divider :vertical="true"></v-divider>
              </div>
              <div v-if="phaseItems.length > 0">
                <v-row>
                  <v-col cols="12">
                    <v-combobox v-model="selectedPhases" :items="phaseItems"
                                label="Phase" :rules="[rules.required]" @click="$refs.pharma_pipelines_filter_form.validate()"
                                chips multiple></v-combobox>
                  </v-col>
                </v-row>
                <v-divider :vertical="true"></v-divider>
              </div>
              <v-row>
                <v-col cols="12">
                  <v-text-field v-model="pharmaPipelineSearch"
                                @update:model-value="pharmaPipelineSearchUpdated"
                                label="Search"
                                placeholder="Search"
                                hide-details filled dense></v-text-field>
                </v-col>
              </v-row>
            </v-form>
          </v-list>
        </v-col>
      </v-row>
    </v-card>
  </v-navigation-drawer>
</template>

<script>
import {defineComponent} from "vue";
import axios from "axios";
import {GChart} from 'vue-google-charts'

const ANY = 'Any'

function debounce(func, timer, timeout = 1000) {
  return (...args) => {
    if (timer) {
      clearTimeout(timer);
    }
    return setTimeout(() => { func.apply(this, args); }, timeout);
  };
}

export default defineComponent({
  name: 'PharmaPipelinesPage',
  components: {
    GChart
  },
  data: () => ({
    filter_drawer: false,
    itemsPerPage: 15,
    sortBy: [{ key: 'company', order: 'asc' }],
    search: '',
    loading: true,
    totalItems: 0,
    headers: [
      {
        title: 'Company',
        align: 'start',
        sortable: true,
        key: 'company'
      },
      {
        title: 'Therapeutic area',
        align: 'start',
        sortable: false,
        key: 'therapeuticArea'
      },
      {
        title: 'Drug',
        align: 'start',
        sortable: true,
        key: 'drug'
      },
      {
        title: 'Indication',
        align: 'start',
        sortable: true,
        key: 'indication'
      },
      {
        title: 'Phase',
        align: 'start',
        sortable: true,
        key: 'phase'
      }
    ],
    pharmaPipelineItems: [],
    companyItems: [ANY],
    selectedCompanies: [ANY],
    therapeuticAreaItems: [ANY],
    selectedTherapeuticAreas: [ANY],
    phaseItems: [ANY],
    selectedPhases: [ANY],
    pharmaPipelineSearch: '',
    pharmaPipelineSearchTimer: undefined,
    rules: {
      required(value) {
        if (value instanceof Array && (value.length === 0)) {
          return 'Required.';
        }
        return !!value || 'Required.';
      }
    },
    companiesChartData: [],
    companiesChartOptions: {
      title: 'Companies',
      pieSliceText: 'value',
      is3D: false,
      backgroundColor: {
        fill: '#364c5b'
      },
      height: 400,
      colors: ['#a6cee3', '#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c'],
      titleTextStyle: {
        fontSize: 16,
        color: '#dedede',
        bold: true
      },
      legend: {
        position: 'top',
        alignment: 'end',
        maxLines: 5,
        textStyle: {
          color: '#c0c0c0',
          fontSize: 12
        },
        scrollArrows: {
          activeColor: '#f0f0f0',
          inactiveColor: 'c0c0c0'
        },
        pagingTextStyle: {
          color: '#f0f0f0'
        }
      }
    },
    therapeuticAreasChartData: [],
    therapeuticAreasChartOptions: {
      title: 'Therapeutic areas',
      pieSliceText: 'value',
      is3D: false,
      backgroundColor: {
        fill: '#364c5b'
      },
      height: 400,
      colors: ['#a6cee3', '#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c'],
      titleTextStyle: {
        fontSize: 16,
        color: '#dedede',
        bold: true
      },
      legend: {
        position: 'top',
        alignment: 'end',
        maxLines: 5,
        textStyle: {
          color: '#c0c0c0',
          fontSize: 12
        },
        scrollArrows: {
          activeColor: '#f0f0f0',
          inactiveColor: 'c0c0c0'
        },
        pagingTextStyle: {
          color: '#f0f0f0'
        }
      }
    },
    phasesChartData: [],
    phasesChartOptions: {
      title: 'Phases',
      pieSliceText: 'value',
      is3D: false,
      backgroundColor: {
        fill: '#364c5b'
      },
      height: 400,
      colors: ['#a6cee3', '#b2df8a', '#fb9a99', '#fdbf6f', '#cab2d6', '#ffff99', '#1f78b4', '#33a02c'],
      titleTextStyle: {
        fontSize: 16,
        color: '#dedede',
        bold: true
      },
      legend: {
        position: 'top',
        alignment: 'end',
        maxLines: 5,
        textStyle: {
          color: '#c0c0c0',
          fontSize: 12
        },
        scrollArrows: {
          activeColor: '#f0f0f0',
          inactiveColor: 'c0c0c0'
        },
        pagingTextStyle: {
          color: '#f0f0f0'
        }
      }
    }
  }),
  watch: {
    selectedCompanies(newSelectedCompanies, oldSelectedCompanies) {
      if (((newSelectedCompanies.length === 1) && (newSelectedCompanies[0] === ANY)) ||
          (!newSelectedCompanies.includes(ANY) && !oldSelectedCompanies.includes(ANY))) {
        this.filterPharmaPipelineItems()
        return
      }

      if (newSelectedCompanies.includes(ANY) && !oldSelectedCompanies.includes(ANY)) {
        this.selectedCompanies = [ANY]
      } else if (newSelectedCompanies.includes(ANY) && oldSelectedCompanies.includes(ANY)) {
        this.selectedCompanies = this.selectedCompanies.filter(o => o !== ANY)
      }
      this.filterPharmaPipelineItems()
    },
    selectedTherapeuticAreas(newSelectedTherapeuticAreas, oldSelectedTherapeuticAreas) {
      if (((newSelectedTherapeuticAreas.length === 1) && (newSelectedTherapeuticAreas[0] === ANY)) ||
          (!newSelectedTherapeuticAreas.includes(ANY) && !oldSelectedTherapeuticAreas.includes(ANY))) {
        this.filterPharmaPipelineItems()
        return
      }

      if (newSelectedTherapeuticAreas.includes(ANY) && !oldSelectedTherapeuticAreas.includes(ANY)) {
        this.selectedTherapeuticAreas = [ANY]
      } else if (newSelectedTherapeuticAreas.includes(ANY) && oldSelectedTherapeuticAreas.includes(ANY)) {
        this.selectedTherapeuticAreas = this.selectedTherapeuticAreas.filter(o => o !== ANY)
      }
      this.filterPharmaPipelineItems()
    },
    selectedPhases(newSelectedPhases, oldSelectedPhases) {
      if (((newSelectedPhases.length === 1) && (newSelectedPhases[0] === ANY)) ||
          (!newSelectedPhases.includes(ANY) && !oldSelectedPhases.includes(ANY))) {
        this.filterPharmaPipelineItems()
        return
      }

      if (newSelectedPhases.includes(ANY) && !oldSelectedPhases.includes(ANY)) {
        this.selectedPhases = [ANY]
      } else if (newSelectedPhases.includes(ANY) && oldSelectedPhases.includes(ANY)) {
        this.selectedPhases = this.selectedPhases.filter(c => c !== ANY)
      }
      this.filterPharmaPipelineItems()
    }
  },
  methods: {
    async loadPharmaPipelines(page, itemsPerPage, sortBy) {
      let data = axios.get('/pharma-pipelines',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              company: this.selectedCompanies.includes(ANY) ? undefined : this.selectedCompanies.join(";"),
              therapeutic_area: this.selectedTherapeuticAreas.includes(ANY) ? undefined : this.selectedTherapeuticAreas.join(","),
              phase: this.selectedPhases.includes(ANY) ? undefined : this.selectedPhases.join(","),
              search: this.pharmaPipelineSearch === '' ? undefined : this.pharmaPipelineSearch,
              page: page,
              items_per_page: itemsPerPage,
              sort_by: sortBy.map(sort => sort.key + ':' + sort.order).join(",")
            }
          });
      return data.then((response) => {
        return {
          items: response.data.items,
          total: response.data.total
        }
      });
    },

    async loadPharmaPipelinesMetaData() {
      return axios.get('/pharma-pipelines-meta-data',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              company: this.selectedCompanies.includes(ANY) ? undefined : this.selectedCompanies.join(";"),
              therapeutic_area: this.selectedTherapeuticAreas.includes(ANY) ? undefined : this.selectedTherapeuticAreas.join(","),
              phase: this.selectedPhases.includes(ANY) ? undefined : this.selectedPhases.join(","),
              search: this.pharmaPipelineSearch === '' ? undefined : this.pharmaPipelineSearch
            }
          });
    },

    downloadExcel() {
      this.loading = true
      axios.get('/pharma-pipelines-excel',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              company: this.selectedCompanies.includes(ANY) ? undefined : this.selectedCompanies.join(";"),
              therapeutic_area: this.selectedTherapeuticAreas.includes(ANY) ? undefined : this.selectedTherapeuticAreas.join(","),
              phase: this.selectedPhases.includes(ANY) ? undefined : this.selectedPhases.join(","),
              search: this.pharmaPipelineSearch === '' ? undefined : this.pharmaPipelineSearch,
              sort_by: this.sortBy.map(sort => sort.key + ':' + sort.order).join(",")
            },
            responseType: 'blob'
          })
          .then((response) => {
            const href = URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = href;
            link.setAttribute('target', '_blank');
            link.click();
            URL.revokeObjectURL(href);
          })
          .finally(() => {
            this.loading = false
          });
    },

    async loadStaticData() {
      return await Promise.all([
        this.loadPharmaPipelinesMetaData()
      ]).then((response) => {
        this.companyItems = response[0].data.allCompanies;
        this.companyItems.unshift(ANY)
        this.therapeuticAreaItems = response[0].data.allTherapeuticAreas;
        this.therapeuticAreaItems.unshift(ANY)
        this.phaseItems = response[0].data.allPhases;
        this.phaseItems.unshift(ANY)
        this.companiesChartData = response[0].data.companyCounts;
        this.therapeuticAreasChartData = response[0].data.therapeuticAreaCounts;
        this.phasesChartData = response[0].data.phaseCounts;
        console.log('Static data loaded: PharmaPipelines')
      })
    },

    loadItems({ page, itemsPerPage, sortBy }) {
      this.loading = true
      return Promise.all([this.loadPharmaPipelines(page, itemsPerPage, sortBy).then(({items, total}) => {
        this.pharmaPipelineItems = items;
        this.totalItems = total;
      }), this.loadPharmaPipelinesMetaData().then((response => {
        this.companiesChartData = response.data.companyCounts;
        this.therapeuticAreasChartData = response.data.therapeuticAreaCounts;
        this.phasesChartData = response.data.phaseCounts;
      }))]).finally(() => {
        this.loading = false
        console.log('Pharma pipelines loaded')
      })
    },

    filterPharmaPipelineItems() {
      // Triggers an event for searching
      this.search = String(Date.now())
    },

    rowProps() {
      return {
        class: "ciencia_row"
      };
    },

    toggleAllOneCompanies(company) {
      if ((this.selectedCompanies.length === 1) && (this.selectedCompanies[0] === company)) {
        this.selectedCompanies = [...this.companyItems]
      } else {
        this.selectedCompanies = [company]
      }
    },

    toggleAllOneTherapeuticAreas(therapeuticArea) {
      if ((this.selectedTherapeuticAreas.length === 1) && (this.selectedTherapeuticAreas[0] === therapeuticArea)) {
        this.selectedTherapeuticAreas = [...this.therapeuticAreaItems]
      } else {
        this.selectedTherapeuticAreas = [therapeuticArea]
      }
    },

    toggleAllOnePhases(phase) {
      if ((this.selectedPhases.length === 1) && (this.selectedPhases[0] === phase)) {
        this.selectedPhases = [...this.phaseItems]
      } else {
        this.selectedPhases = [phase]
      }
    },

    pharmaPipelineSearchUpdated() {
      this.pharmaPipelineSearchTimer = debounce(() => this.filterPharmaPipelineItems(), this.pharmaPipelineSearchTimer)()
    },

    async companiesChartReady(chart, google) {
      const setCompaniesChartSelection = (selectedRow) => {
        let selectedCompany = this.companiesChartData[selectedRow + 1][0]
        if (selectedCompany !== undefined) {
          this.toggleAllOneCompanies(selectedCompany)
        }
      }

      const companiesSelectHandler = function () {
        const selection = chart.getSelection()
        if (selection.length === 0) {
          return
        }
        const selectedItem = selection[0];
        setCompaniesChartSelection(selectedItem.row)
      }

      google.visualization.events.addListener(chart, 'select', companiesSelectHandler);
    },

    async therapeuticAreasChartReady(chart, google) {
      const setTherapeuticAreasChartSelection = (selectedRow) => {
        let selectedTherapeuticArea = this.therapeuticAreasChartData[selectedRow + 1][0]
        if (selectedTherapeuticArea !== undefined) {
          this.toggleAllOneTherapeuticAreas(selectedTherapeuticArea)
        }
      }

      const therapeuticAreasSelectHandler = function () {
        const selection = chart.getSelection()
        if (selection.length === 0) {
          return
        }
        const selectedItem = selection[0];
        setTherapeuticAreasChartSelection(selectedItem.row)
      }

      google.visualization.events.addListener(chart, 'select', therapeuticAreasSelectHandler);
    },

    async phasesChartReady(chart, google) {
      const setPhasesChartSelection = (selectedRow) => {
        let selected_phase = this.phasesChartData[selectedRow + 1][0]
        if (selected_phase !== undefined) {
          this.toggleAllOnePhases(selected_phase.v)
        }
      }

      const phasesSelectHandler = function () {
        const selection = chart.getSelection()
        if (selection.length === 0) {
          return
        }
        const selectedItem = selection[0];
        setPhasesChartSelection(selectedItem.row)
      }

      google.visualization.events.addListener(chart, 'select', phasesSelectHandler);
    }
  },
  async mounted() {
    await this.loadStaticData()
  }
});
</script>

<style lang="scss" scoped>
.error {
  color: indianred;
}

::v-deep .ciencia_row:hover {
  background-color: #566c7b;
}
</style>
